/**
 * @file     MarketAndHistorySections.tsx
 * @purpose  Two additional standalone cards for the property detail page:
 *           <MarketDemandSection> (buyer↔seller gradient gauge) and
 *           <ListingHistorySection> (tabbed: Listing history table / Price
 *           changes). These are SEPARATE cards, rendered below the "About this
 *           home" card — same family as your existing derived panels
 *           (MarketStoryPanel, etc.). If they should be members-only, wrap them
 *           in <PremiumPanelGate> exactly like the other derived bands.
 *
 *           ACCENT #4DB2BA, 36px icon chip, eyebrow + title header, ~16px card.
 */
'use client';

import { layoutTokens } from '@/design-system/tokens/layout';
import * as React from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, keyframes } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useReveal, type Reveal } from './useReveal';
import { DisclosureSection } from './DisclosureSection';

const ACCENT = '#4DB2BA';
/** Glyphs and text: ACCENT is 2.5:1 on a light surface; the strong accent clears 3:1 in both themes. */
const ACCENT_INK = 'var(--r-accent-strong)';
const EASE = 'cubic-bezier(.22,.61,.36,1)';

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: none; }
`;

function Card({ children, reveal }: { children: React.ReactNode; reveal: Reveal }) {
  const { mode } = useAppTheme();
  const isDark = mode === 'dark';
  return (
    <Box
      ref={reveal.ref}
      sx={{
        bgcolor: 'background.paper',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.08)'}`,
        borderRadius: `${layoutTokens.radius.lg}px`,
        boxShadow: isDark ? '0 18px 50px rgba(0,0,0,0.5)' : '0 14px 40px rgba(0,0,0,0.07)',
        p: { xs: '24px 22px', md: '30px 34px' },
        opacity: reveal.shown ? 1 : 0,
        transform: reveal.shown ? 'none' : 'translateY(18px)',
        transition: reveal.reduce ? 'none' : `opacity .55s ${EASE}, transform .55s ${EASE}`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Box>
  );
}

function Header({ icon, eyebrow, title }: { icon: React.ReactNode; eyebrow: string; title: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.6} sx={{ mb: 3 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: `${layoutTokens.radius.md}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(ACCENT, 0.08), border: `1px solid ${alpha(ACCENT, 0.16)}`, color: ACCENT_INK, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: ACCENT_INK }}>{eyebrow}</Typography>
        <Typography component="h3" sx={{ fontSize: '1.1875rem', fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</Typography>
      </Box>
    </Stack>
  );
}

/* ── Market demand gauge ─────────────────────────────────────────────────────
 * `demandIndex`: 0 (deep buyer's market) → 100 (deep seller's market). */
/** Shared readout mapping — also feeds the "The area" card headline strip. */
export function demandReadout(demandIndex: number): string {
  const pct = Math.max(2, Math.min(98, demandIndex));
  return pct < 38 ? 'Buyer-leaning' : pct > 62 ? 'Seller-leaning' : 'Balanced';
}

export function MarketDemandSection({
  demandIndex,
  caption,
}: { demandIndex: number; caption?: string }) {
  const reveal = useReveal();
  const pct = Math.max(2, Math.min(98, demandIndex));
  const readout = demandReadout(demandIndex);
  // Pin eases from the centre to its true position; gauge "draws" in from the left.
  const pinLeft = reveal.shown || reveal.reduce ? `${pct}%` : '50%';
  return (
    <Card reveal={reveal}>
      <Header
        icon={<Box component="svg" viewBox="0 0 24 24" sx={{ width: 19, height: 19 }} fill="currentColor"><path d="M3.5 18.5l6-6 4 4L22 6.92 20.59 5.5l-7.09 8-4-4L2 17z" /></Box>}
        eyebrow="Market"
        title="Market demand in this area"
      />
      <Box sx={{ px: 0.5 }}>
        <Box sx={{ position: 'relative', height: 22, mb: 1 }}>
          <Box
            sx={{
              position: 'absolute', left: pinLeft, transform: 'translateX(-50%)', bottom: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              opacity: reveal.shown ? 1 : 0,
              transition: reveal.reduce ? 'none' : `left .9s ${EASE} .15s, opacity .5s ease .3s`,
              willChange: 'left',
            }}
          >
            <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: ACCENT_INK, whiteSpace: 'nowrap', mb: 0.25 }}>{readout}</Typography>
            <Box component="svg" viewBox="0 0 24 24" sx={{ width: 20, height: 20 }} fill={ACCENT_INK}><path d="M12 2C8.1 2 5 5.1 5 9c0 4 7 13 7 13s7-9 7-13c0-3.9-3.1-7-7-7z" /></Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: 12, borderRadius: `${layoutTokens.radius.pill}px`,
            background: 'linear-gradient(90deg,#3E8EF7 0%,#3E8EF7 26%,#34C759 42%,#34C759 60%,#FF6B45 80%,#FF3B30 100%)',
            transform: reveal.shown ? 'scaleX(1)' : 'scaleX(0)',
            transformOrigin: 'left',
            transition: reveal.reduce ? 'none' : `transform .8s ${EASE}`,
            willChange: 'transform',
          }}
        />
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.4 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Buyer’s market</Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Balanced</Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500 }}>Seller’s market</Typography>
        </Stack>
        {caption && <Typography sx={{ mt: 2, fontSize: '0.84rem', lineHeight: 1.55, color: 'text.secondary' }}>{caption}</Typography>}
      </Box>
    </Card>
  );
}

/* ── Listing history (tabbed) ────────────────────────────────────────────── */

export interface HistoryRow {
  start: string;
  end: string | null;
  price: string;
  event: string;
  isActive?: boolean; // colors the event label
  listingId: string;
}
export interface PriceChange {
  date: string;
  from: string;
  to: string;
  delta: string; // "−4.3%"
  isCut?: boolean;
}

/** One-line summary for the collapsed history row, derived from what the
 *  section already receives. No em-dashes; middot separators. */
function historySummary(rows: HistoryRow[], priceChanges: PriceChange[]): string {
  const years = rows
    .map((r) => r.start.match(/\d{4}/)?.[0])
    .filter((y): y is string => !!y)
    .map(Number);
  const since = years.length > 0 ? Math.min(...years) : null;
  const times = rows.length === 1 ? 'once' : `${rows.length} times`;
  const head = since != null ? `Listed ${times} since ${since}` : `Listed ${times}`;
  const last = priceChanges[0];
  return last ? `${head} · last change ${last.delta}` : `${head} · no price changes`;
}

export function ListingHistorySection({
  propertyLabel,
  rows,
  priceChanges,
  isAuthenticated = true,
  variant = 'disclosure',
  headless = false,
}: {
  propertyLabel: string;
  rows: HistoryRow[];
  priceChanges: PriceChange[];
  isAuthenticated?: boolean;
  /** Expanded only: drop the heading when a titled ListingSection wraps the table. */
  headless?: boolean;
  /**
   * 'disclosure' (default): own Card with a collapsed summary row (map
   * slide-up / legacy call sites). 'expanded': no card chrome, no collapse;
   * heading + tabs + tables render immediately (detail-page Overview tab).
   */
  variant?: 'disclosure' | 'expanded';
}) {
  const { t } = useTranslation();
  const { mode } = useAppTheme();
  const isDark = mode === 'dark';
  const reveal = useReveal();
  const gated = !isAuthenticated;
  const gateSx = gated ? { filter: 'blur(5px)', userSelect: 'none' as const, pointerEvents: 'none' as const, transition: 'filter .35s' } : undefined;
  const [tab, setTab] = React.useState<'history' | 'changes'>('history');
  const divider = `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'}`;
  const sunk = isDark ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.03)';
  const stagger = (i: number) => (reveal.reduce ? {} : { animation: `${fadeSlide} .4s ease both`, animationDelay: `${i * 45}ms` });

  const tabSx = (active: boolean) => ({
    flex: 1, border: 'none', background: 'none', cursor: 'pointer', font: 'inherit',
    fontSize: '0.875rem', fontWeight: 500, py: 2, transition: 'color .2s',
    color: active ? ACCENT_INK : 'text.disabled',
    '&:hover': { color: active ? ACCENT_INK : 'text.secondary' },
  });
  const cols = '1.2fr 1.2fr 1fr 1fr 1.1fr';
  const th = { px: 2, py: 1.6, fontSize: '0.75rem', fontWeight: 600 } as const;
  const td = { px: 2, py: 2, fontSize: '0.84rem', fontVariantNumeric: 'tabular-nums' } as const;

  const body = (
    <>
      <Box sx={{ position: 'relative', display: 'flex', borderBottom: divider, mb: 3, mt: 1 }}>
        <Box component="button" onClick={() => setTab('history')} sx={tabSx(tab === 'history')}>Listing history</Box>
        <Box component="button" onClick={() => setTab('changes')} sx={tabSx(tab === 'changes')}>Price changes ({priceChanges.length})</Box>
        <Box
          aria-hidden
          sx={{
            position: 'absolute', bottom: -1, height: 2, width: '50%', bgcolor: ACCENT, borderRadius: `${layoutTokens.radius.pill}px`,
            left: tab === 'history' ? '0%' : '50%',
            transition: reveal.reduce ? 'none' : `left .28s ${EASE}`,
          }}
        />
      </Box>

      {tab === 'history' ? (
        <Box key="history" sx={reveal.reduce ? undefined : { animation: `${fadeSlide} .3s ease both` }}>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', mb: 2 }}>
            Buy / sell history for {propertyLabel}
          </Typography>
          <Box sx={{ border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, borderRadius: `${layoutTokens.radius.lg}px`, overflow: 'hidden' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: cols, bgcolor: sunk, borderBottom: divider }}>
              <Box sx={th}>Date start</Box>
              <Box sx={th}>Date end</Box>
              <Box sx={{ ...th, textAlign: 'right' }}>Price</Box>
              <Box sx={{ ...th, textAlign: 'center' }}>Event</Box>
              <Box sx={{ ...th, textAlign: 'right' }}>Listing ID</Box>
            </Box>
            {rows.length === 0 ? (
              <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled', py: 2, px: 2 }}>
                {t('propertyDetail.history.empty', { defaultValue: 'No listing history on record for this home.' })}
              </Typography>
            ) : (
            <Box aria-hidden={gated || undefined} sx={gateSx}>
            {rows.map((r, i) => (
              <Box key={`${r.listingId}-${i}`} sx={{ display: 'grid', gridTemplateColumns: cols, borderTop: i === 0 ? 'none' : divider, alignItems: 'center', ...stagger(i) }}>
                <Typography sx={{ ...td, fontWeight: 500 }}>{r.start}</Typography>
                <Typography sx={{ ...td, color: 'text.disabled' }}>{r.end ?? '—'}</Typography>
                <Typography sx={{ ...td, fontWeight: 500, textAlign: 'right' }}>{r.price}</Typography>
                <Box sx={{ textAlign: 'center', px: 2, py: 2 }}>
                  <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 500, color: r.isActive ? '#1B9C73' : 'text.disabled' }}>{r.event}</Typography>
                </Box>
                <Typography sx={{ ...td, color: 'text.secondary', textAlign: 'right' }}>{r.listingId}</Typography>
              </Box>
            ))}
            </Box>
            )}
          </Box>
        </Box>
      ) : (
        <Box key="changes" sx={reveal.reduce ? undefined : { animation: `${fadeSlide} .3s ease both` }}>
          <Typography sx={{ fontSize: '0.84rem', color: 'text.secondary', mb: 2 }}>Price changes during the current listing</Typography>
          {priceChanges.length === 0 ? (
            <Typography sx={{ fontSize: '0.875rem', color: 'text.disabled', py: 2 }}>No price changes recorded for this listing.</Typography>
          ) : <Box aria-hidden={gated || undefined} sx={gateSx}>{priceChanges.map((p, i) => (
            <Stack key={i} direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 2, p: '15px 16px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`, borderRadius: `${layoutTokens.radius.lg}px`, mb: 1, ...stagger(i) }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{p.date}</Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: '0.84rem', color: 'text.disabled', textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>{p.from}</Typography>
                <Box component="span" sx={{ color: 'text.disabled' }}>→</Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{p.to}</Typography>
                <Box component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: p.isCut === false ? '#D7881E' : '#1B9C73', bgcolor: alpha(p.isCut === false ? '#D7881E' : '#1B9C73', 0.1), px: 1.1, py: 0.4, borderRadius: `${layoutTokens.radius.pill}px` }}>{p.delta}</Box>
              </Stack>
            </Stack>
          ))}</Box>}
        </Box>
      )}
    </>
  );

  if (variant === 'expanded') {
    return (
      <Box ref={reveal.ref} sx={{ mt: headless ? 0 : 2 }}>
        {headless ? null : (
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
            Listing history
          </Typography>
        )}
        {body}
      </Box>
    );
  }

  return (
    <Card reveal={reveal}>
      <DisclosureSection
        id="listing-history"
        sheetTitle="Listing history"
        summaryAriaLabel="Listing history details"
        summary={
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              Listing history
            </Typography>
            <Typography component="span" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
              {historySummary(rows, priceChanges)}
            </Typography>
          </Stack>
        }
      >
        {body}
      </DisclosureSection>
    </Card>
  );
}
